// German WAIS-IV subtest scoring utility.
//
// Converts a subtest raw-score sum into its Wertpunkt (scaled score, 1-19) using
// the German norm tables (Tabelle A.1), then derives a z-score and percentile
// rank (Prozentrang). Mirrors the French db/WAIS-ATT-MDT/wais-scoring.js, but the
// norms live in per-subtest parquets under src/db/WAIS-IV-DE/ and are read at
// runtime through the DuckDB IPC bridge (window.electronAPI.queryParquet) instead
// of being embedded — the parquets are the single source of truth.
//
// Age range: 16;0–89;11. Subtests BZF, FW and DT are only normed up to 69;11
// (their columns are NA for older groups → those lookups return an error).

export const AGE_GROUPS = [
  "16-17", "18-19", "20-24", "25-29", "30-34", "35-44", "45-54",
  "55-64", "65-69", "70-74", "75-79", "80-84", "85-89",
];

export const SUBTESTS = [
  "mt", "gf", "zn", "mz", "wt", "rd", "sys", "vp", "aw", "zst",
  "bzf", "fw", "av", "dt", "be",
];

// Subtests not normed for ages 70+ (parquet columns are NA there).
export const SUBTESTS_UNDER_70_ONLY = ["bzf", "fw", "dt"];

export const SUBTEST_INFO = {
  mt:  { abbr: "MT",  de: "Mosaik-Test",            en: "Block Design" },
  gf:  { abbr: "GF",  de: "Gemeinsamkeiten finden", en: "Similarities" },
  zn:  { abbr: "ZN",  de: "Zahlen nachsprechen",    en: "Digit Span" },
  mz:  { abbr: "MZ",  de: "Matrizen-Test",          en: "Matrix Reasoning" },
  wt:  { abbr: "WT",  de: "Wortschatz-Test",        en: "Vocabulary" },
  rd:  { abbr: "RD",  de: "Rechnerisches Denken",   en: "Arithmetic" },
  sys: { abbr: "SYS", de: "Symbolsuche",            en: "Symbol Search" },
  vp:  { abbr: "VP",  de: "Visuelle Puzzles",       en: "Visual Puzzles" },
  aw:  { abbr: "AW",  de: "Allgemeines Wissen",     en: "Information" },
  zst: { abbr: "ZST", de: "Zahlen-Symbol-Test",     en: "Coding" },
  bzf: { abbr: "BZF", de: "Buchstaben-Zahlen-Folgen", en: "Letter-Number Sequencing" },
  fw:  { abbr: "FW",  de: "Formenwaage",            en: "Figure Weights" },
  av:  { abbr: "AV",  de: "Allgemeines Verständnis", en: "Comprehension" },
  dt:  { abbr: "DT",  de: "Durchstreich-Test",      en: "Cancellation" },
  be:  { abbr: "BE",  de: "Bilder ergänzen",        en: "Picture Completion" },
};

/** Map a fractional age (years) to its norm age-group key, or null if out of range. */
export function getAgeGroup(age) {
  if (age == null || age < 16 || age >= 90) return null;
  if (age < 18) return "16-17";
  if (age < 20) return "18-19";
  if (age < 25) return "20-24";
  if (age < 30) return "25-29";
  if (age < 35) return "30-34";
  if (age < 45) return "35-44";
  if (age < 55) return "45-54";
  if (age < 65) return "55-64";
  if (age < 70) return "65-69";
  if (age < 75) return "70-74";
  if (age < 80) return "75-79";
  if (age < 85) return "80-84";
  return "85-89";
}

// Standard normal CDF (Abramowitz & Stegun 7.1.26) — same approximation as the
// French module, so percentiles are computed identically across both norms.
function normcdf(z) {
  if (z < -6) return 0;
  if (z > 6) return 1;
  const a1 = 0.254829592, a2 = -0.284496736, a3 = 1.421413741, a4 = -1.453152027, a5 = 1.061405429, p = 0.3275911;
  const sign = z < 0 ? -1 : 1;
  const x = Math.abs(z) / Math.SQRT2;
  const t = 1 / (1 + p * x);
  const erf = 1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return 0.5 * (1 + sign * erf);
}

const round2 = (n) => Math.round(n * 100) / 100;

/** Wertpunkt → { z, pr } (mean 10, SD 3). */
export function wpToScores(wp) {
  const z = (wp - 10) / 3;
  return { z: round2(z), pr: round2(normcdf(z) * 100) };
}

/**
 * Find the Wertpunkt for a raw score in a subtest's parquet rows.
 * Raw scores are clamped to [0, maxRaw]; out-of-range high scores take the top WP.
 * Pure (no IPC) so it can be unit-tested with injected rows.
 * @returns {number|null} the Wertpunkt, or null if the age group is not normed.
 */
export function lookupWP(rows, raw, ageGroup) {
  if (!rows || rows.length === 0) return null;
  const maxRaw = rows.length - 1;
  const clamped = Math.max(0, Math.min(raw, maxRaw));
  const row = rows.find((r) => r.raw_score === clamped);
  const wp = row ? row[ageGroup] : null;
  return wp == null ? null : wp;
}

// Per-subtest parquet rows, loaded once on first use.
const _cache = {};
async function loadRows(sub) {
  if (_cache[sub]) return _cache[sub];
  if (!window?.electronAPI?.queryParquet) {
    throw new Error("queryParquet IPC nicht verfügbar");
  }
  const res = await window.electronAPI.queryParquet(`WAIS-IV-DE/${sub}.parquet`);
  if (!res.success) throw new Error(res.error);
  _cache[sub] = res.rows;
  return res.rows;
}

/**
 * Score a single German WAIS-IV subtest.
 * @param {string} sub - subtest key (see SUBTESTS), case-insensitive
 * @param {number|string} raw - raw-score sum
 * @param {number} age - patient age in years
 * @returns {Promise<{subtest,abbr,raw,ageGroup,wp,z,pr} | {error}>}
 */
export async function scoreSubtest_DE(sub, raw, age) {
  const key = String(sub).toLowerCase();
  if (!SUBTESTS.includes(key)) return { error: `Unbekannter Untertest: ${sub}` };

  const ageGroup = getAgeGroup(age);
  if (!ageGroup) return { error: `Alter ${age}: ausserhalb des Normbereichs (16–89 Jahre)` };

  const rawNum = parseInt(raw, 10);
  if (Number.isNaN(rawNum)) return { error: "Ungültiger Rohwert" };

  const rows = await loadRows(key);
  const wp = lookupWP(rows, rawNum, ageGroup);
  if (wp == null) {
    return { error: `${SUBTEST_INFO[key].abbr}: keine Normwerte für Altersgruppe ${ageGroup}` };
  }

  return { subtest: key, abbr: SUBTEST_INFO[key].abbr, raw: rawNum, ageGroup, wp, ...wpToScores(wp) };
}

// --- Längste Zahlenspanne (longest digit span) percentile rank ---------------
// kind → längste-Spanne parquet (cumulative % of sample reaching ≥ span).
// emcd = vorwärts (LZ-V), emci = rückwärts (LZ-R), emcc = sequentiell (LZ-S).
const SPAN_FILE = { emcd: "emcd", emci: "emci", emcc: "emcc" };

/**
 * Percentile-rank range for a longest span from cumulative-% rows.
 * cum(span) = % of sample reaching ≥ span, so 100−cum(span) is the % scoring
 * below (lower bound) and 100−cum(span+1) the upper bound. Pure (no IPC).
 * @returns {[number|null, number]} [low, high]
 */
export function spanPRFromRows(rows, empan, ageGroup) {
  const cum = (e) => {
    const row = rows.find((r) => r.empan === e);
    const v = row ? row[ageGroup] : null;
    return v == null || Number.isNaN(v) ? null : v;
  };
  const cumThis = cum(empan);
  const cumNext = cum(empan + 1);
  const low = cumThis != null ? Math.round(100 - cumThis) : null;
  const high = cumNext != null ? Math.round(100 - cumNext) : 100;
  return [low, high];
}

/**
 * Score a longest-digit-span condition (Spanne PR).
 * @param {number} age - patient age in years
 * @param {number|string} empan - longest span length (items)
 * @param {string} kind - "emcd" (forward) | "emci" (backward) | "emcc" (sequential)
 * @returns {Promise<{kind,empan,ageGroup,pc:[number|null,number]} | {error}>}
 */
export async function scoreSpan_DE(age, empan, kind) {
  const file = SPAN_FILE[kind];
  if (!file) return { error: `Unbekannte Spanne: ${kind}` };

  const ageGroup = getAgeGroup(age);
  if (!ageGroup) return { error: `Alter ${age}: ausserhalb des Normbereichs (16–89 Jahre)` };

  const n = parseInt(empan, 10);
  if (Number.isNaN(n)) return { error: "Ungültige Spanne" };

  const rows = await loadRows(file);
  return { kind, empan: n, ageGroup, pc: spanPRFromRows(rows, n, ageGroup) };
}

/**
 * Score one or more German WAIS-IV subtests.
 * @param {number} age - patient age in years
 * @param {Object} rawScores - raw scores keyed by subtest, e.g. { mt: 34, zst: 70 }
 * @param {string|string[]} [tests='all'] - 'all', a single key, or an array of keys
 * @returns {Promise<{age,ageGroup,subtests:Object,error?:string}>}
 */
export async function scoreWAIS_IV_DE(age, rawScores, tests = "all") {
  const ageGroup = getAgeGroup(age);
  const result = { age, ageGroup, subtests: {} };
  if (!ageGroup) {
    result.error = `Alter ${age}: ausserhalb des Normbereichs (16–89 Jahre)`;
    return result;
  }

  let requested;
  if (tests === "all") requested = SUBTESTS;
  else if (Array.isArray(tests)) requested = tests.map((s) => String(s).toLowerCase());
  else requested = [String(tests).toLowerCase()];

  for (const sub of requested) {
    const raw = rawScores?.[sub];
    if (raw == null || raw === "") continue;
    result.subtests[sub] = await scoreSubtest_DE(sub, raw, age);
  }
  return result;
}
